import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Label } from "@/components/ui";
import { authApi } from "@/services/api";
import { useAuthStore } from "@/stores/auth.store";
import { Loader2, Check, Circle, Lock } from "lucide-react";
import {
  changePasswordSchema,
  checkPasswordRequirements,
  PASSWORD_REQUIREMENTS,
  type ChangePasswordInput,
} from "@/lib/validations";

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const { user, clearMustChangePassword } = useAuthStore();
  const [apiError, setApiError] = useState("");

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting, isValid },
  } = useForm<ChangePasswordInput>({
    resolver: zodResolver(changePasswordSchema),
    mode: "onChange",
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const newPassword = watch("newPassword");
  const confirmPassword = watch("confirmPassword");

  // Verificar requisitos em tempo real
  const requirements = useMemo(
    () => checkPasswordRequirements(newPassword || ""),
    [newPassword]
  );

  const passwordsMatch = newPassword === confirmPassword && newPassword?.length > 0;

  async function onSubmit(data: ChangePasswordInput) {
    setApiError("");

    try {
      await authApi.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });

      // Atualiza o store para remover a flag mustChangePassword
      clearMustChangePassword();

      // Redireciona para o dashboard
      if (user?.type === "company") {
        navigate("/dashboard");
      } else {
        navigate("/admin/dashboard");
      }
    } catch (err: any) {
      setApiError(err.message || "Erro ao alterar senha");
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Lock className="w-6 h-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">Defina sua senha</CardTitle>
          <p className="text-sm text-muted-foreground mt-2">
            Olá, {user?.name}!
            <br />
            Por segurança, você precisa definir uma nova senha para continuar.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {apiError && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                {apiError}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="currentPassword">Senha atual</Label>
              <Input
                id="currentPassword"
                type="password"
                placeholder="Digite sua senha temporária"
                autoComplete="current-password"
                {...register("currentPassword")}
              />
              {errors.currentPassword && (
                <p className="text-sm text-destructive">{errors.currentPassword.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword">Nova senha</Label>
              <Input
                id="newPassword"
                type="password"
                placeholder="Digite sua nova senha"
                autoComplete="new-password"
                {...register("newPassword")}
              />
              {errors.newPassword && (
                <p className="text-sm text-destructive">{errors.newPassword.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar nova senha</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirme sua nova senha"
                autoComplete="new-password"
                {...register("confirmPassword")}
              />
              {errors.confirmPassword && (
                <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
              )}
              {confirmPassword && !passwordsMatch && !errors.confirmPassword && (
                <p className="text-sm text-destructive">As senhas não coincidem</p>
              )}
            </div>

            <div className="p-4 bg-muted rounded-md space-y-2">
              <p className="text-sm font-medium mb-2">Requisitos da senha:</p>
              {PASSWORD_REQUIREMENTS.map((req) => {
                const isValid = requirements[req.key as keyof typeof requirements];
                return (
                  <div
                    key={req.key}
                    className={`flex items-center gap-2 text-sm ${
                      isValid ? "text-green-600" : "text-muted-foreground"
                    }`}
                  >
                    {isValid ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Circle className="w-4 h-4" />
                    )}
                    {req.label}
                  </div>
                );
              })}
            </div>

            <Button type="submit" className="w-full" disabled={!isValid || isSubmitting}>
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Salvar e Continuar
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
